import React, { useEffect, useState, useMemo } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'utils/dayjs';

import { fetchBreadcrumbs, fetchSeoMeta } from 'actions';
import {
  getDisplayValue,
  DEFAULT_MOBILE_DATE_FORMAT,
  DEFAULT_SOURCE_CURRENCY,
  navigateCurrencyDatePicker,
  prefetchCurrencyYearArchive,
  parseDate,
  formatParsedDate,
} from 'utils';

import { useSeoMeta, useSelectorProps } from 'hooks';

import DatePicker from 'components/commons/DatePicker';
import { Table } from 'components/Table';
import { MbChart } from 'components/MbChart';
import ContentPlaceholder from 'components/ContentPlaceholder';
import Icon from 'components/commons/Icon';

import { currencies } from 'constants/currency';

import { TableFooter } from 'components/Table/styles';
import {
  Wrapper,
  Header,
  TitleWrapper,
  Update,
  PrimaryTitle,
  Text,
  HashLink,
  Source,
  SubTitle,
} from './styles';

const titles = ['code', 'сurrency', 'name', 'course'];

const daysForDeclination = {
  среда: 'среда',
  пятница: 'пятница',
  суббота: 'суббота',
};

const terms = [
  { type: 'sell', value: 'banks' },
  { type: 'nbu', value: 'nbu' },
  { type: 'buy', value: 'banks' },
];

const getHref = currency => `/nbu/${currency}/`;

const filterByWhiteList = (data, activeKey) => {
  const keys = activeKey ? [activeKey] : Object.values(currencies);
  const whiteList = new Map(keys.map(item => [item, item]));

  return data
    .filter(el => whiteList.has(el.code.toLowerCase()))
    .sort((a, b) => a.iso - b.iso);
};

const CurrencyNbuTable = () => {
  const params = useParams();

  const [date, setDate] = useState(params.date);

  const history = useHistory();
  const { locale, formatMessage } = useIntl();

  const dispatch = useDispatch();

  const selectorProps = useSelectorProps();
  const { currency = DEFAULT_SOURCE_CURRENCY } = params;

  const { pageTitle, pageDescription } = useSeoMeta();
  const data = useSelector(state =>
    filterByWhiteList(state.currency?.nbuCurrencyList?.data, currency)
  );

  useEffect(() => {
    if (params.date !== date) {
      setDate(params.date);
    }
  }, [params.date]);

  useEffect(() => {
    dispatch(fetchSeoMeta(selectorProps));
    dispatch(fetchBreadcrumbs(selectorProps));
  }, [currency]);

  const onDateAccept = e => {
    const parsedDate = parseDate(e);

    if (!parsedDate) {
      return;
    }

    const formattedDate = parsedDate.format(DEFAULT_MOBILE_DATE_FORMAT);
    const legacyPath = currency
      ? `/nbu/${currency}/${formattedDate}/`
      : `/nbu/${formattedDate}/`;
    navigateCurrencyDatePicker({
      history,
      selectedDate: formattedDate,
      currency,
      legacyPath,
      locale,
    });
  };

  const onDatePickerOpen = () => {
    prefetchCurrencyYearArchive({
      selectedDate: date,
      currency,
      locale,
    });
  };

  const getRate = code => {
    const res = data
      .filter(el => el.code === code)
      .map(el => el.rate)
      .join('');

    return res.split('.').join(',');
  };

  const getTrend = code => {
    return data
      .filter(el => el.code === code)
      .map(el => el.trend)
      .join('');
  };

  const getIfChange = code => {
    const trend = Number(getTrend(code));

    if (!(trend <= 0.00009 && trend >= -0.00009)) return 1;

    return 0;
  };

  const getUah = code => {
    const trend = getTrend(code);
    const split = trend.split('.');
    const uah = String(split[0])
      .split('')
      .pop();

    return uah;
  };

  const ifDrawUah = code => {
    const uah = getUah(code);

    if (uah > 0) return 1;
    return 0;
  };

  const getCent = code => {
    const trend = getTrend(code);
    const split = trend.split('.');
    const afterDot = String(split[1]).split('');
    afterDot.splice(2, 0, '.');
    const join = afterDot.join('');

    if (Math.round(join) < 1) return Number(join).toFixed(1);

    return Math.round(join);
  };

  const getTrendDiff = code => {
    const trend = Number(getTrend(code));

    if (trend < 0 && trend > -0.15)
      return formatMessage({ id: 'nbu.autogeneratingText.fellSlightly' });
    if (trend <= -0.15 && trend > -1)
      return formatMessage({ id: 'nbu.autogeneratingText.dropped' });
    if (trend <= -1)
      return formatMessage({ id: 'nbu.autogeneratingText.droppedSharply' });
    if (trend === 0 || (trend <= 0.00009 && trend >= -0.00009))
      return formatMessage({ id: 'nbu.autogeneratingText.notChange' });
    if (trend > 0 && trend < 0.15)
      return formatMessage({ id: 'nbu.autogeneratingText.increasedSlightly' });
    if (trend > 0.15 && trend < 1)
      return formatMessage({ id: 'nbu.autogeneratingText.increased' });

    return formatMessage({ id: 'nbu.autogeneratingText.roseSharply' });
  };

  const getDeclination = code => {
    const cent = getCent(code);

    const splitted = String(cent).split('');

    if (cent >= 10 && cent <= 20) return formatMessage({ id: 'cents' });
    if (splitted[splitted.length - 1] === '1')
      return formatMessage({ id: 'oneCent' });
    if (
      splitted[splitted.length - 1] >= 2 &&
      splitted[splitted.length - 1] <= 4
    )
      return formatMessage({ id: 'fewCents' });

    return formatMessage({ id: 'cents' });
  };

  const getDay = () => {
    const day = formatParsedDate(date, 'dddd', locale);

    if (day === daysForDeclination[day]) {
      const splitted = day.split('');
      splitted.pop();
      splitted.push('у');

      return splitted.join('');
    }

    return day;
  };

  const usdAutogeneratingText = useMemo(
    () =>
      formatMessage(
        { id: 'nbu.usd.autogeneratingText' },
        {
          day: `${getDay()}`,
          date: `${formatParsedDate(date, 'DD MMMM YYYY', locale)}`,
          trendCurr: `${getTrendDiff('USD')}`,
          amount: `${getUah('USD')}`,
          isUah: `${ifDrawUah('USD')}`,
          centAmount: `${getCent('USD')}`,
          status: `${getIfChange('USD')}`,
          rate: `${getRate('USD')}`,
          declination: `${getDeclination('USD')}`,
        }
      ),
    [date, data]
  );

  const eurAutogeneratingText = useMemo(
    () =>
      formatMessage(
        { id: 'nbu.eur.autogeneratingText' },
        {
          trendCurr: `${getTrendDiff('EUR')}`,
          amount: `${getUah('EUR')}`,
          isUah: `${ifDrawUah('EUR')}`,
          centAmount: `${getCent('EUR')}`,
          status: `${getIfChange('EUR')}`,
          rate: `${getRate('EUR')}`,
          declination: `${getDeclination('EUR')}`,
        }
      ),
    [data]
  );

  const plnAutogeneratingText = useMemo(
    () =>
      formatMessage(
        { id: 'nbu.pln.autogeneratingText' },
        {
          trendCurr: `${getTrendDiff('PLN')}`,
          amount: `${getUah('PLN')}`,
          isUah: `${ifDrawUah('PLN')}`,
          centAmount: `${getCent('PLN')}`,
          status: `${getIfChange('PLN')}`,
          rate: `${getRate('PLN')}`,
          declination: `${getDeclination('PLN')}`,
        }
      ),
    [data]
  );

  const shouldShowText = !currency || (!date && !currency);

  return (
    <>
      <Wrapper>
        <Header>
          <TitleWrapper>
            <Update>
              <Icon name="watch" style={{ margin: '0 5px 1px 0' }} />
              <FormattedMessage id="updated" />
              {` `}
              {data[0]?.date &&
                data[0]?.time &&
                dayjs(
                  `${data[0].date} ${data[0].time}`,
                  'DD.MM.YYYY HH:mm'
                ).format('DD.MM.YYYY, HH:mm')}
            </Update>

            <SubTitle>{pageDescription}</SubTitle>
            <PrimaryTitle>{pageTitle}</PrimaryTitle>
          </TitleWrapper>
          <DatePicker
            type="outline"
            value={date}
            onChange={setDate}
            getDisplayValue={getDisplayValue}
            onOpen={onDatePickerOpen}
            onAccept={e => onDateAccept(e)}
          />
        </Header>

        <Table
          titles={titles}
          rows={data}
          activeCurrency={currency}
          type="nbu"
          getHref={getHref}
        />
        <TableFooter>
          <HashLink smooth to="#anotherCurr">
            <FormattedMessage id="anotherCurr" />
          </HashLink>

          <Source>
            <FormattedMessage id="barGraph.source" />
          </Source>
        </TableFooter>
        {shouldShowText && (
          <Text>
            {data.length <= 0 && (
              <>
                <ContentPlaceholder style={{ width: '95%', height: '15px' }} />
                <ContentPlaceholder style={{ width: '95%', height: '15px' }} />
                <ContentPlaceholder style={{ width: '95%', height: '15px' }} />
                <ContentPlaceholder style={{ width: '95%', height: '15px' }} />
                <ContentPlaceholder style={{ width: '95%', height: '15px' }} />
                <ContentPlaceholder style={{ width: '95%', height: '15px' }} />
              </>
            )}
            {data.length > 0 && (
              <>
                {usdAutogeneratingText}
                {eurAutogeneratingText}
                {plnAutogeneratingText}
              </>
            )}
          </Text>
        )}
        <MbChart
          terms={terms}
          type="nbu"
          chartTitle={
            currency
              ? 'dynamic.rates.chart.nbu.title.currency'
              : 'dynamic.rates.chart.nbu.title'
          }
        />
      </Wrapper>
    </>
  );
};

export default CurrencyNbuTable;
